import i18next from 'i18next';
import {
  convert24TimeTo12,
  DateFormatter,
  getReadingTypeLabel,
  parseContactAvailability,
} from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import { mmToFeetInchesString } from 'infrastructure/functions/measurements';
import { splitByUppercase } from 'infrastructure/functions/split-by-uppercase';

import type { IChangeLog } from 'infrastructure/interfaces';

const dateFields = [
  'endDate',
  'startDate',
  'birthDate',
  'statusUpdatedAt',
  'otsStartDate',
];
const dateTimeFields = ['scheduledDate', 'startTime', 'endTime'];

const specialFields = ['ccmStatus', 'ccmClaimStatus', 'pcmStatus'];

interface ISpecialists {
  guid: string;
  fullName: string;
}

export class ChangeLog {
  field: string;
  from: string;
  to: string;

  constructor(data: IChangeLog, section?: string) {
    const { field, from, to } = data;
    this.field = specialFields.includes(field)
      ? splitByUppercase(field)
          .map((word, index) => (index === 0 ? word.toUpperCase() : word))
          .join(' ')
      : splitByUppercase(field).join(' ');
    this.from = from;
    this.to = to;

    if (field === 'invoiceNPI') {
      this.field = i18next.t(`labels.invoiceNpi`);
    }

    if (field === 'claimStatus' || field === 'ccmClaimStatus') {
      this.from = i18next.exists(`claimStatuses.${from}`)
        ? i18next.t(`claimStatuses.${from}` as any)
        : from;
      this.to = i18next.exists(`claimStatuses.${to}`)
        ? i18next.t(`claimStatuses.${to}` as any)
        : to;
    }

    if (field === 'ccmSpecialists') {
      const localFrom = JSON.parse(from) as unknown as ISpecialists[];
      const localTo = JSON.parse(to) as unknown as ISpecialists[];
      this.from = localFrom.map((el) => el.fullName).join(', ');
      this.to = localTo.map((el) => el.fullName).join(', ');
    }
    if (field === 'martialStatus') {
      this.from = i18next.exists(`maritalStatuses.${from}`)
        ? i18next.t(`maritalStatuses.${from}` as any)
        : from;
      this.to = i18next.exists(`maritalStatuses.${to}`)
        ? i18next.t(`maritalStatuses.${to}` as any)
        : to;
    }
    if (field === 'gender') {
      this.from = i18next.exists(`genders.${from}`)
        ? i18next.t(`genders.${from}` as any)
        : from;
      this.to = i18next.exists(`genders.${to}`)
        ? i18next.t(`genders.${to}` as any)
        : to;
    }
    if (field === 'preferredContactMethod') {
      this.from = i18next.exists(`contactMethods.${from}`)
        ? i18next.t(`contactMethods.${from}` as any)
        : from;
      this.to = i18next.exists(`contactMethods.${to}`)
        ? i18next.t(`contactMethods.${to}` as any)
        : to;
    }
    if (field === 'appointmentMethod') {
      this.from = i18next.exists(`appointmentMethod.${from}`)
        ? i18next.t(`appointmentMethod.${from}` as any)
        : from;
      this.to = i18next.exists(`appointmentMethod.${to}`)
        ? i18next.t(`appointmentMethod.${to}` as any)
        : to;
    }
    if (field === 'weekMealStatus') {
      this.from = i18next.exists(`weekMealStatuses.${from}`)
        ? i18next.t(`weekMealStatuses.${from}` as any)
        : from;
      this.to = i18next.exists(`weekMealStatuses.${to}`)
        ? i18next.t(`weekMealStatuses.${to}` as any)
        : to;
    }
    if (field === 'race') {
      this.from = i18next.exists(`races.${from}`)
        ? i18next.t(`races.${from}` as any)
        : from;
      this.to = i18next.exists(`races.${to}`)
        ? i18next.t(`races.${to}` as any)
        : to;
    }
    if (field === 'workingStatus') {
      this.from = i18next.exists(`workingStatuses.${from}`)
        ? i18next.t(`workingStatuses.${from}` as any)
        : from;
      this.to = i18next.exists(`workingStatuses.${to}`)
        ? i18next.t(`workingStatuses.${to}` as any)
        : to;
    }
    if (
      field === 'emergencyContactNumberType' ||
      field === 'secondaryNumberType' ||
      field === 'numberType'
    ) {
      this.from = i18next.exists(`numberTypes.${from}`)
        ? i18next.t(`numberTypes.${from}` as any)
        : from;
      this.to = i18next.exists(`numberTypes.${to}`)
        ? i18next.t(`numberTypes.${to}` as any)
        : to;
    }
    if (field === 'ccmStatus') {
      this.from = i18next.exists(`ccmStatuses.${from}`)
        ? i18next.t(`ccmStatuses.${from}` as any)
        : from;
      this.to = i18next.exists(`ccmStatuses.${to}`)
        ? i18next.t(`ccmStatuses.${to}` as any)
        : to;
    }
    if (field === 'status') {
      this.from = i18next.exists(`rpmStatuses.${from}`)
        ? i18next.t(`rpmStatuses.${from}` as any)
        : from;
      this.to = i18next.exists(`rpmStatuses.${to}`)
        ? i18next.t(`rpmStatuses.${to}` as any)
        : to;
    }

    if (section && section === 'PatientHoldingStatus' && field === 'status') {
      this.from = i18next.exists(`holdingStatuses.${from}`)
        ? i18next.t(`holdingStatuses.${from}` as any)
        : from;
      this.to = i18next.exists(`holdingStatuses.${to}`)
        ? i18next.t(`holdingStatuses.${to}` as any)
        : to;
    }

    if (field === 'invoiceTerms') {
      this.from = i18next.exists(`clinicInvoiceTerms.${from}`)
        ? i18next.t(`clinicInvoiceTerms.${from}` as any)
        : from;
      this.to = i18next.exists(`clinicInvoiceTerms.${to}`)
        ? i18next.t(`clinicInvoiceTerms.${to}` as any)
        : to;
    }
    if (field === 'invoiceProductStatus') {
      const getLabel = (condition: string) =>
        condition === 'true'
          ? i18next.t('labels.active')
          : i18next.t('labels.inactive');

      this.from = getLabel(from);
      this.to = getLabel(to);
    }
    if (field === 'invoiceProductBillingType') {
      this.from = i18next.exists(`clinicInvoiceBillingTypes.${from}`)
        ? i18next.t(`clinicInvoiceBillingTypes.${from}` as any)
        : from;
      this.to = i18next.exists(`clinicInvoiceBillingTypes.${to}`)
        ? i18next.t(`clinicInvoiceBillingTypes.${to}` as any)
        : to;
    }

    if (field === 'invoiceProductServiceType') {
      this.from = i18next.exists(`clinicInvoiceServiceTypes.${from}`)
        ? i18next.t(`clinicInvoiceServiceTypes.${from}` as any)
        : from;
      this.to = i18next.exists(`clinicInvoiceServiceTypes.${to}`)
        ? i18next.t(`clinicInvoiceServiceTypes.${to}` as any)
        : to;
    }
    if (field === 'customerType') {
      const parserFrom = JSON.parse(from);
      const parserTo = JSON.parse(to);
      this.from = parserFrom?.name;
      this.to = parserTo?.name;
    }

    if (field === 'readingTypes') {
      const parsedFrom: Array<string> | undefined = JSON.parse(from);
      const parsedTo: Array<string> | undefined = JSON.parse(to);

      this.from =
        parsedFrom && parsedFrom.length
          ? parsedFrom.map((el) => getReadingTypeLabel(el)).join(', ')
          : from;
      this.to =
        parsedTo && parsedTo.length
          ? parsedTo.map((el) => getReadingTypeLabel(el)).join(', ')
          : to;
    }
    if (dateFields.includes(field)) {
      this.from = DateFormatter({
        date: from,
        format: DateFormats['MM-dd-yyyy'],
      });
      this.to = DateFormatter({
        date: to,
        format: DateFormats['MM-dd-yyyy'],
      });
    }
    if (dateTimeFields.includes(field)) {
      this.from = DateFormatter({
        date: from,
        format: DateFormats['MM-dd-yyyy h:mm a'],
      });
      this.to = DateFormatter({
        date: to,
        format: DateFormats['MM-dd-yyyy h:mm a'],
      });
    }
    if (field === 'takingReadingTime') {
      this.field = i18next.t('labels.timeForTakingReading');
      this.from = from && from !== 'null' ? convert24TimeTo12(from) : from;
      this.to = convert24TimeTo12(to);
    }
    if (field === 'weightGoalLbs') {
      this.field = i18next.t('labels.weightGoal');
      this.from = from && from !== 'null' ? `${from} lbs` : from;
      this.to = `${to} lbs`;
    }
    if (field === 'heightMillimeters') {
      this.field = i18next.t('labels.height');
      this.from = from && from !== 'null' ? mmToFeetInchesString(from) : from;
      this.to = to && to !== 'null' ? mmToFeetInchesString(to) : to;
    }
    if (field === 'reviewTimeSeconds') {
      const formatReviewTime = (value: string) => {
        const seconds = Number(value);
        if (Number.isFinite(seconds)) {
          const minutes = seconds / 60;
          const label =
            minutes > 1
              ? i18next.t('labels.minutes')
              : i18next.t('labels.minute');
          return `${minutes} ${label}`;
        }
        return value;
      };
      this.field = i18next.t('labels.clinicReviewTime');
      this.from = formatReviewTime(from);
      this.to = formatReviewTime(to);
    }
    if (field === 'contactAvailability') {
      this.from = parseContactAvailability(this.from);
      this.to = parseContactAvailability(this.to);
    }
  }
}
